import type {
  DBAccessory,
  DBBBOrg,
  DBBusinessContact,
  DBBusinessContactRecipient,
  DBCard,
  DBCardAccessoryFields,
  DBCardImage,
  DBCardPreset,
  DBCardSave,
  DBContact,
  DBContactNote,
  DBEmailSignature,
  DBEmailSignatureBanner,
  DBEmailSignatureContactDetailsField,
  DBEmailSignatureContactDetailsFields,
  DBEmailSignatureDesign,
  DBEmailSignatureEnvironmentalFooter,
  DBEmailSignatureImage,
  DBEmailSignatureImages,
  DBEmailSignatureLegalDisclaimer,
  DBEmailSignaturePersonalDetailsFields,
  DBEmailSignatureSaveContact,
  DBEmailSignatureSocialLink,
  DBEmailSignatureSocialLinks,
  DBEmailSignatureV2,
  DBExportedTo,
  DBGeneratedCardForEmailSignature,
  DBIntegration,
  DBPersonalAccessToken,
  DBStripeSubscription,
  DBTeamEmailSignatureBulkCreateRequest,
  DBUser,
  DBUserProvisioningAttributeFilter,
  DBUserProvisioningPresetFilter,
  DBUserTags,
  DBVirtualBackground,
  DBVirtualBackgroundImage,
} from '@blinq-db-types';
import {
  type Accessory,
  type AnonymousUser,
  type BBOrg,
  type BusinessContact,
  type BusinessContactRecipient,
  type Card,
  type CardAccessoryFields,
  type CardImage,
  CardLayout,
  type CardMetadata,
  type CardPreset,
  type CardSave,
  type Contact,
  type ContactNote,
  type EmailSignatureField,
  type ExportedTo,
  type Integration,
  type PersonalAccessToken,
  type StripeSubscription,
  type User,
  type UserProvisioningAttributeFilter,
  type UserProvisioningPresetFilter,
  type UserTags,
} from '@blinq-types';
import type {
  EmailSignatureBanner,
  EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFields,
  EmailSignatureDesign,
  EmailSignatureEnvironmentalFooter,
  EmailSignatureImage,
  EmailSignatureLegalDisclaimer,
  EmailSignaturePersonalDetailsFields,
  EmailSignatureSaveContact,
  EmailSignatureSocialLink,
  EmailSignatureSocialLinks,
  EmailSignatureV2,
  EmailSignaturesImages,
} from '@components/EmailSignaturev2/types';
import type {
  VirtualBackground,
  VirtualBackgroundImage,
} from '@components/VirtualBackgrounds/types';
import { IntegrationServices } from '@constants/integration';
import { generateEntityId } from '@modules/helpers';

import type {
  GeneratedCardForEmailSignature,
  TeamEmailSignatureBulkCreateRequest,
} from './../components/EmailSignaturev2/types';
import { populateCardFieldOrder, populateCardPresetFieldOrder } from './card-helpers';
import { dbTimestamps } from './timestamp-utils';

export function toUser(dbUser: DBUser): User {
  return {
    userId: dbUser.user_id,
    firstName: dbUser.first_name,
    lastName: dbUser.last_name,
    bbOrgId: dbUser.bb_org_id,
    creationTimestampMs: dbUser.creation_timestamp_ms,
    avatarUrl: dbUser.avatar_url,
    //currentWorkspaceId: undefined,
    currentCardId: dbUser.current_card_id,
    cardIds: dbUser.card_ids ?? [],
    orgIds: dbUser.org_ids,
    isAnonymous: dbUser.is_anonymous ?? false,
    country: dbUser.country,
    companySize: dbUser.company_size,
    signInEmailAddress: dbUser.sign_in_email_address,
    providerId: dbUser.provider_id,
    timezone: dbUser.timezone,
    uiEvents: {
      showBusinessOnboarding: dbUser.ui_events?.show_business_onboarding ?? false,
      appDownloaded: dbUser.ui_events?.app_downloaded ?? false,
    },
    shouldNotReceiveRecipientEmails: dbUser.should_not_receive_recipient_emails ?? false,
    isIosDevice: dbUser.is_ios_device ?? undefined,
    masqueradingOrgId: dbUser.masquerading_org_id ?? undefined,
  };
}

export function toDBUser(user: User | AnonymousUser): DBUser {
  return sanitizeForDb({
    first_name: user.firstName,
    last_name: user.lastName,
    user_id: user.userId,
    avatar_url: user.avatarUrl,
    card_ids: user.cardIds,
    creation_timestamp_ms: user.creationTimestampMs,
    current_card_id: user.currentCardId,
    org_ids: user.orgIds,
    bb_org_id: user.bbOrgId,
    is_anonymous: user.isAnonymous,
    country: user.country,
    company_size: user.companySize,
    ui_events: user.uiEvents
      ? {
          show_business_onboarding: user.uiEvents.showBusinessOnboarding,
          app_downloaded: user.uiEvents.appDownloaded,
        }
      : undefined,
    provider_id: user.providerId,
    sign_in_email_address: user.signInEmailAddress,
    should_not_receive_recipient_emails: user.shouldNotReceiveRecipientEmails,
    timezone: user.timezone,
    gclid: user.gclid,
    fbclid: user.fbclid,
    is_ios_device: user.isIosDevice,
  });
}

const DEFAULT_CARD_COLOR = '#F45A57';
const DEFAULT_CARD_TITLE = 'Work';

export const fieldsToEmailSignature = (
  fields: { value: string; id: string; isPreset?: boolean }[],
): EmailSignatureField =>
  fields.reduce<Record<string, { show: boolean; columns: number }>>((map, obj) => {
    map[obj.id] = { show: true, columns: 1 };
    return map;
  }, {});

function toCardImage(dbCardImage: DBCardImage | null | undefined): CardImage {
  return {
    editedImageUrl: dbCardImage?.edited_img_url,
    originalImageUrl: dbCardImage?.original_img_url,
    editorMetadata: dbCardImage?.editor_metadata
      ? {
          backgroundColor: dbCardImage.editor_metadata.background_color,
          createdAt: dbCardImage.editor_metadata.created_at,
          imgPosition: dbCardImage.editor_metadata.img_positition,
          updatedAt: dbCardImage.editor_metadata.updated_at,
          zoom: dbCardImage.editor_metadata.zoom,
          editorVersion: dbCardImage.editor_metadata.editor_version,
        }
      : undefined,
  };
}

export function toCard(dbCard: DBCard): Card {
  const generatedEmails: Card['emails'] = dbCard.emails
    ? dbCard.emails.map((email) => ({
        id: email.id ?? generateEntityId(),
        label: email.label,
        value: email.value,
        isPreset: email.is_preset ?? false,
        linkedTo: email.linked_to,
      }))
    : [];

  const generatedAddresses: Card['addresses'] = dbCard.addresses
    ? dbCard.addresses.map((address) => ({
        id: address.id ?? generateEntityId(),
        label: address.label,
        value: address.value,
        type: address.type,
        isPreset: address.is_preset ?? false,
        linkedTo: address.linked_to,
      }))
    : [];

  const generatedUrls: Card['urls'] = dbCard.urls
    ? dbCard.urls
        .map((url) => ({
          id: url.id ?? generateEntityId(),
          title: url.title,
          value: url.value,
          type: url.type,
          isPreset: url.is_preset ?? false,
          linkedTo: url.linked_to,
          userValue: url.user_value ?? url.value,
        }))
        .filter((url) => url.type !== 'referral')
    : [];

  const generatedPhoneNumbers: Card['phoneNumbers'] = dbCard.phone_numbers
    ? dbCard.phone_numbers.map((phoneNumber) => ({
        id: phoneNumber.id ?? generateEntityId(),
        label: phoneNumber.label,
        value: phoneNumber.value,
        isPreset: phoneNumber.is_preset ?? false,
        linkedTo: phoneNumber.linked_to,
        extension: phoneNumber.extension,
        countryCode: phoneNumber.country_code ?? undefined,
      }))
    : [];

  return {
    cardId: dbCard.card_id,
    cardEditId: dbCard.card_edit_id,
    cardUpdateId: dbCard.card_update_id,
    cardTitle: dbCard.card_title ?? DEFAULT_CARD_TITLE,
    orgId: dbCard.org_id,
    profileId: dbCard.profile_id,
    userId: dbCard.user_id ?? undefined,
    referralLinkEnabled: dbCard.referral_link_enabled ?? false,
    blinqMessageEnabled: dbCard.blinq_message_enabled ?? true,
    whereWeMetEnabled: dbCard.where_we_met_enabled ?? false,
    isInvisible: dbCard.is_invisible ?? false,
    isPaused: dbCard.is_paused ?? false,
    color: dbCard.color ?? DEFAULT_CARD_COLOR,
    firstName: dbCard.first_name || '',
    middleName: dbCard.middle_name,
    lastName: dbCard.last_name,
    honorificPrefix: dbCard.honorific_prefix,
    honorificSuffix: dbCard.honorific_suffix,
    maidenName: dbCard.maiden_name,
    preferredName: dbCard.preferred_name,
    pronouns: dbCard.pronouns,
    jobTitle: dbCard.job_title,
    orgName: dbCard.org_name,
    department: dbCard.department,
    accreditations: dbCard.accreditations,
    headline: dbCard.headline,
    profilePictureImg: toCardImage(dbCard.profile_picture_img),
    companyLogoImg: toCardImage(dbCard.company_logo_img),
    creationTimestampMs: dbCard.creation_timestamp_ms ?? undefined,
    updatedTimestampMs: dbCard.updated_timestamp_ms,
    cardPresetId: dbCard.card_preset_id ?? undefined,
    recipientEmail: dbCard.recipient_email,
    referrerCardId: dbCard.referrer_card_id,
    referrerUserId: dbCard.referrer_user_id,
    createdBy: dbCard.created_by,
    bbActivationTimestampMs: dbCard.bb_activation_timestamp_ms ?? undefined,
    layoutImagesLocked: dbCard.layout_images_locked,
    logoIsInQr: dbCard.logo_is_in_qr ?? false,
    logoInQrUrl: dbCard.logo_in_qr_url,
    cardIsEditable: dbCard.card_is_editable ?? true,
    lockedFields: dbCard.locked_fields ?? [],
    lockedFieldTypes: dbCard.locked_field_types ?? [],
    allFieldTypesLocked: dbCard.all_field_types_locked ?? false,
    lockedEmailSignatureFields: dbCard.locked_email_signature_fields ?? [],
    allEmailSignatureFieldsLocked: dbCard.all_email_signature_fields_locked ?? false,
    colorLocked: dbCard.color_locked ?? false,
    qrCodeLocked: dbCard.qr_code_locked ?? false,
    activationEmailSentAt: dbCard.activation_email_sent_at,
    linkedAttributes: dbCard.linked_attributes ?? [],
    layout: dbCard.layout,
    coverPhotoImg: toCardImage(dbCard.cover_photo_img),
    userProfileId: dbCard.user_profile_id,
    emails: generatedEmails,
    addresses: generatedAddresses,
    urls: generatedUrls,
    phoneNumbers: generatedPhoneNumbers,
    emailSignature: dbCard.email_signature
      ? {
          signatureId: dbCard.email_signature?.signature_id ?? '',
          qrCode: dbCard.email_signature?.qr_code ?? true,
          companyLogo:
            dbCard.email_signature?.company_logo ?? Boolean(dbCard.company_logo_url) ?? false,
          profilePicture:
            dbCard.email_signature?.profile_picture ?? Boolean(dbCard.profile_picture_url) ?? false,
          headline: dbCard.email_signature?.headline ?? Boolean(dbCard.headline) ?? false,
          jobTitle: dbCard.email_signature?.job_title ?? Boolean(dbCard.job_title) ?? false,
          accreditations:
            dbCard.email_signature?.accreditations ?? Boolean(dbCard.accreditations) ?? false,
          orgName: dbCard.email_signature?.org_name ?? Boolean(dbCard.org_name) ?? false,
          phoneNumbers:
            dbCard.email_signature?.phone_numbers ?? fieldsToEmailSignature(generatedPhoneNumbers),
          emails: dbCard.email_signature?.emails ?? fieldsToEmailSignature(generatedEmails),
          urls: dbCard.email_signature?.urls ?? fieldsToEmailSignature(generatedUrls),
          addresses:
            dbCard.email_signature?.addresses ?? fieldsToEmailSignature(generatedAddresses),
          customBanner: dbCard.email_signature?.custom_banner ?? '',
        }
      : undefined,
    fieldOrder: populateCardFieldOrder({
      fieldOrder: dbCard.field_order,
      addresses: generatedAddresses,
      emails: generatedEmails,
      urls: generatedUrls,
      phoneNumbers: generatedPhoneNumbers,
    }),
  };
}

export function toGeneratedCardForEmailSignature(
  generatedCard: DBGeneratedCardForEmailSignature,
): GeneratedCardForEmailSignature {
  const card = toCard(generatedCard);

  return {
    ...card,
    emailSignatureId: generatedCard.email_signature_id,
    isInvisible: generatedCard.is_invisible,
  };
}

function toDBCardImage(img: CardImage | null | undefined): DBCardImage | null {
  if (!img) {
    return null;
  }
  // if all falsy, just return null
  if (!img?.editedImageUrl && !img.originalImageUrl && img.editorMetadata) {
    return null;
  }
  const timestamps = dbTimestamps(img.editorMetadata?.createdAt);
  return {
    edited_img_url: img.editedImageUrl,
    original_img_url: img.originalImageUrl,
    editor_metadata: img.editorMetadata
      ? {
          editor_version: img.editorMetadata.editorVersion,
          background_color: img.editorMetadata.backgroundColor,
          zoom: img.editorMetadata.zoom,
          img_positition: img.editorMetadata.imgPosition,
          ...timestamps,
        }
      : null,
  };
}

export function toDBCard(card: Card): DBCard {
  return {
    card_id: card.cardId,
    card_edit_id: card.cardEditId,
    card_title: card.cardTitle,
    profile_id: card.profileId,
    org_id: card.orgId,
    user_id: card.userId,
    accreditations: card.accreditations,
    activation_email_sent_at: card.activationEmailSentAt,
    addresses: card.addresses?.map((address) => ({
      id: address.id,
      label: address.label,
      value: address.value,
      type: address.type,
      is_preset: address.isPreset,
      ...(address.linkedTo ? { linked_to: address.linkedTo } : {}),
    })),
    all_email_signature_fields_locked: card.allEmailSignatureFieldsLocked,
    all_field_types_locked: card.allFieldTypesLocked,
    bb_activation_timestamp_ms: card.bbActivationTimestampMs ?? undefined,
    blinq_message_enabled: card.blinqMessageEnabled,
    card_is_editable: card.cardIsEditable,
    card_preset_id: card.cardPresetId,
    color_locked: card.colorLocked,
    color: card.color,
    company_logo_img: toDBCardImage(card.companyLogoImg),
    // company_logo_url: card.companyLogoImg?.editedImageUrl || null, // dont backwards compat here
    cover_photo_img: toDBCardImage(card.coverPhotoImg),
    created_by: card.createdBy,
    creation_timestamp_ms: card.creationTimestampMs ?? undefined,
    department: card.department,
    email_signature: card.emailSignature ? toDBEmailSignature(card.emailSignature) : undefined,
    first_name: card.firstName,
    headline: card.headline,
    honorific_prefix: card.honorificPrefix,
    honorific_suffix: card.honorificSuffix,
    is_invisible: card.isInvisible,
    is_paused: card.isPaused,
    job_title: card.jobTitle,
    last_name: card.lastName,
    layout: card.layout || CardLayout.Layout_1C, // ensure not undefined
    layout_images_locked: card.layoutImagesLocked,
    linked_attributes: card.linkedAttributes,
    locked_email_signature_fields: card.lockedEmailSignatureFields,
    locked_field_types: card.lockedFieldTypes,
    locked_fields: card.lockedFields,
    logo_in_qr_url: card.logoInQrUrl,
    logo_is_in_qr: card.logoIsInQr,
    maiden_name: card.maidenName,
    middle_name: card.middleName,
    org_name: card.orgName,
    preferred_name: card.preferredName,
    profile_picture_img: toDBCardImage(card.profilePictureImg),
    // profile_picture_url: card.profilePictureImg?.editedImageUrl || null, // dont backwards compat here
    // profile_picture_placement: card.companyLogoImg?.editedImageUrl ? "floating" : "banner", // make old clients compatible
    pronouns: card.pronouns,
    qr_code_locked: card.qrCodeLocked,
    recipient_email: card.recipientEmail,
    referral_link_enabled: card.referralLinkEnabled,
    referrer_card_id: card.referrerCardId,
    referrer_user_id: card.referrerUserId,
    updated_timestamp_ms: card.updatedTimestampMs,
    user_profile_id: card.userProfileId,
    where_we_met_enabled: card.whereWeMetEnabled,
    emails: card.emails?.map((email) => ({
      id: email.id,
      label: email.label,
      value: email.value,
      is_preset: email.isPreset,
      ...(email.linkedTo ? { linked_to: email.linkedTo } : {}),
    })),
    urls: card.urls?.map((url) => ({
      id: url.id,
      title: url.title,
      value: url.value,
      type: url.type,
      is_preset: url.isPreset,
      ...(url.linkedTo ? { linked_to: url.linkedTo } : {}),
      user_value: url.userValue,
    })),
    phone_numbers: card.phoneNumbers?.map((phoneNumber) => ({
      id: phoneNumber.id,
      label: phoneNumber.label,
      value: phoneNumber.value,
      country_code: phoneNumber.countryCode ?? null,
      is_preset: phoneNumber.isPreset,
      ...(phoneNumber.extension ? { extension: phoneNumber.extension } : {}),
      ...(phoneNumber.linkedTo ? { linked_to: phoneNumber.linkedTo } : {}),
    })),
    field_order: card.fieldOrder,
  };
}

export function toDBGeneratedCardForEmailSignature(
  generatedCard: GeneratedCardForEmailSignature,
): DBGeneratedCardForEmailSignature {
  const dbCard = toDBCard(generatedCard);

  return {
    ...dbCard,
    email_signature_id: generatedCard.emailSignatureId,
    is_invisible: generatedCard.isInvisible,
  };
}

export function toContact(dbContact: DBContact): Contact {
  return {
    card: toCard(dbContact.card),
    cardEditId: dbContact.card_edit_id,
    cardId: dbContact.card_id,
    contactId: dbContact.contact_id,
    creationTimestampMs: dbContact.creation_timestamp_ms,
    isInvisible: dbContact.is_invisible,
    userId: dbContact.user_id,
    location: dbContact.location,
    recipientCardEditId: dbContact.recipient_card_edit_id,
    recipientCardId: dbContact.recipient_card_id,
    tags: dbContact.tags?.map((t) => ({ value: t.value, id: t.id })),
    source: dbContact.source,
  };
}

export function toAccessory(dbAccessory: DBAccessory): Accessory {
  const accessory = {
    id: dbAccessory.id,
    createdAt: dbAccessory.created_at,
    updatedAt: dbAccessory.updated_at,
    notifiedAt: dbAccessory.notified_at,
    title: dbAccessory.title,
    imageFront: dbAccessory.image_front,
    imageBack: dbAccessory.image_back,
    orgId: dbAccessory.org_id || '',
    userId: dbAccessory.user_id || '',
    cardId: dbAccessory.card_id || '',
    purchasedAt: dbAccessory.purchased_at,
    deletedAt: dbAccessory.deleted_at ?? undefined,
    status: dbAccessory.status,
    createdBy: dbAccessory.created_by ? toUser(dbAccessory.created_by) : undefined,
    orderId: dbAccessory.order_id || undefined,
    type: dbAccessory.type,
  };

  if (dbAccessory.type === 'card_plastic_dark' || dbAccessory.type === 'card_plastic_light') {
    return {
      ...accessory,
      type: dbAccessory.type,
      fields: toAccessoryFields(dbAccessory.fields),
      showBlinqLogo: dbAccessory.show_blinq_logo ?? true,
    };
  }

  return {
    ...accessory,
    type: dbAccessory.type,
  };
}

function toAccessoryFields(dbAccessoryFields: DBCardAccessoryFields): CardAccessoryFields {
  return sanitizeForDb({
    firstName: dbAccessoryFields.first_name,
    lastName: dbAccessoryFields.last_name,
    secondaryField: dbAccessoryFields.secondary_field,
    logo: dbAccessoryFields.logo
      ? sanitizeForDb({
          id: dbAccessoryFields.logo.id,
          value: dbAccessoryFields.logo.value,
          maxWidth: dbAccessoryFields.logo.max_width,
          maxHeight: dbAccessoryFields.logo.max_height,
        })
      : undefined,
  });
}

function toAccessoryDBFields(accessoryFields: CardAccessoryFields): DBCardAccessoryFields {
  return sanitizeForDb({
    first_name: accessoryFields.firstName,
    last_name: accessoryFields.lastName,
    secondary_field: accessoryFields.secondaryField,
    logo: accessoryFields.logo
      ? sanitizeForDb({
          id: accessoryFields.logo.id,
          value: accessoryFields.logo.value,
          max_width: accessoryFields.logo.maxWidth,
          max_height: accessoryFields.logo.maxHeight,
        })
      : undefined,
  });
}

export function toAccessoryDb(
  accessory: Partial<Accessory>,
  isAdmin = false,
): Partial<DBAccessory> {
  let dbAccessory: Partial<DBAccessory>;
  if (accessory.type === 'card_plastic_custom') {
    dbAccessory = sanitizeForDb({
      id: accessory.id,
      org_id: accessory.orgId,
      card_id: accessory.cardId,
      user_id: accessory.userId,
      type: accessory.type,
      created_at: accessory.createdAt,
      updated_at: accessory.updatedAt,
      deleted_at: accessory.deletedAt,
      notified_at: accessory.notifiedAt,
      image_back: accessory.imageBack,
      image_front: accessory.imageFront,
      title: accessory.title,
      created_by: accessory.createdBy ? toDBUser(accessory.createdBy) : undefined,
    });
  } else {
    dbAccessory = sanitizeForDb({
      id: accessory.id,
      org_id: accessory.orgId,
      card_id: accessory.cardId,
      user_id: accessory.userId,
      type: accessory.type,
      fields: accessory.fields ? toAccessoryDBFields(accessory.fields) : undefined,
      created_at: accessory.createdAt,
      updated_at: accessory.updatedAt,
      deleted_at: accessory.deletedAt,
      notified_at: accessory.notifiedAt,
      image_back: accessory.imageBack,
      image_front: accessory.imageFront,
      title: accessory.title,
      show_blinq_logo: accessory.showBlinqLogo,
      created_by: accessory.createdBy ? toDBUser(accessory.createdBy) : undefined,
    });
  }

  if (isAdmin) {
    dbAccessory.purchased_at = accessory.purchasedAt;
    dbAccessory.status = accessory.status;
  }

  return dbAccessory;
}

function toDBEmailSignature(
  emailSignature: CardMetadata['emailSignature'],
): DBEmailSignature | undefined {
  if (!emailSignature) return;

  if (!Object.keys(emailSignature).length) {
    return {};
  }

  return sanitizeForDb({
    signature_id: emailSignature.signatureId,
    addresses: emailSignature.addresses,
    emails: emailSignature.emails,
    urls: emailSignature.urls,
    phone_numbers: emailSignature.phoneNumbers,
    accreditations: emailSignature.accreditations,
    custom_banner: emailSignature.customBanner,
    headline: emailSignature.headline,
    company_logo: emailSignature.companyLogo,
    job_title: emailSignature.jobTitle,
    org_name: emailSignature.orgName,
    profile_picture: emailSignature.profilePicture,
    qr_code: emailSignature.qrCode,
  });
}

export function toBusinessContact(dbBusinessContact: DBBusinessContact): BusinessContact {
  const generatedExportedTo = dbBusinessContact.exported_to
    ? dbBusinessContact.exported_to.map((exportTo) => ({
        service: exportTo.service,
        id: exportTo.id,
        lastExportTimestamp: exportTo.last_export_timestamp,
      }))
    : [];
  return {
    autoImportToBB: dbBusinessContact.auto_import_to_bb,
    bbContactId: dbBusinessContact.bb_contact_id,
    bbSharedTimestampMs: dbBusinessContact.bb_shared_timestamp_ms,
    card: toCard(dbBusinessContact.card),
    cardId: dbBusinessContact.card_id,
    creationTimestampMs: dbBusinessContact.creation_timestamp_ms,
    isInvisible: dbBusinessContact.is_invisible,
    orgId: dbBusinessContact.org_id,
    recipients: transformObjectValues(dbBusinessContact.recipients, toBusinessContactRecipient),
    recipientCardEditIds: dbBusinessContact.recipient_card_edit_ids,
    recipientCardIds: dbBusinessContact.recipient_card_ids,
    referrerCardId: dbBusinessContact.referrer_card_id,
    exportedTo: generatedExportedTo,
  };
}

export function toDBBusinessContact(businessContact: BusinessContact): DBBusinessContact {
  const DBExportedTo = businessContact.exportedTo
    ? businessContact.exportedTo.map((exported) => toDBExportedTo(exported))
    : [];
  return {
    auto_import_to_bb: businessContact.autoImportToBB,
    bb_contact_id: businessContact.bbContactId,
    bb_shared_timestamp_ms: businessContact.bbSharedTimestampMs,
    card: toDBCard(businessContact.card),
    card_id: businessContact.cardId,
    creation_timestamp_ms: businessContact.creationTimestampMs,
    is_invisible: businessContact.isInvisible,
    org_id: businessContact.orgId,
    recipients: transformObjectValues(businessContact.recipients, toDBBusinessContactRecipient),
    recipient_card_edit_ids: businessContact.recipientCardEditIds,
    recipient_card_ids: businessContact.recipientCardIds,
    referrer_card_id: businessContact.referrerCardId,
    exported_to: DBExportedTo,
  };
}
export function toDBExportedTo(dbExportedTo: ExportedTo): DBExportedTo {
  return {
    service: dbExportedTo.service,
    id: dbExportedTo.id,
    external_id: dbExportedTo.externalId,
    integration_id: dbExportedTo.integrationId,
    last_export_timestamp: dbExportedTo.lastExportTimestamp,
  };
}

export function toBusinessContactRecipient(
  dbBusinessContactRecipient: DBBusinessContactRecipient,
): BusinessContactRecipient {
  return {
    contactId: dbBusinessContactRecipient.contact_id,
    autoImportToBB: dbBusinessContactRecipient.auto_import_to_bb,
    bbSharedTimestampMs: dbBusinessContactRecipient.bb_shared_timestamp_ms,
    cardEditId: dbBusinessContactRecipient.card_edit_id,
    creationTimestampMs: dbBusinessContactRecipient.creation_timestamp_ms,
    location: dbBusinessContactRecipient.location,
    recipientCardEditId: dbBusinessContactRecipient.recipient_card_edit_id,
    recipientCardId: dbBusinessContactRecipient.recipient_card_id,
  };
}

export function toDBBusinessContactRecipient(
  businessContactRecipient: BusinessContactRecipient,
): DBBusinessContactRecipient {
  return {
    contact_id: businessContactRecipient.contactId,
    auto_import_to_bb: businessContactRecipient.autoImportToBB,
    bb_shared_timestamp_ms: businessContactRecipient.bbSharedTimestampMs,
    card_edit_id: businessContactRecipient.cardEditId,
    creation_timestamp_ms: businessContactRecipient.creationTimestampMs,
    location: businessContactRecipient.location,
    recipient_card_edit_id: businessContactRecipient.recipientCardEditId,
    recipient_card_id: businessContactRecipient.recipientCardId,
  };
}

export function toCardPreset(dbCardPreset: DBCardPreset): CardPreset {
  const generatedEmails = dbCardPreset.emails
    ? dbCardPreset.emails.map((email) => ({
        id: email.id ?? generateEntityId(),
        label: email.label,
        value: email.value,
      }))
    : [];

  const generatedAddresses = dbCardPreset.addresses
    ? dbCardPreset.addresses.map((address) => ({
        id: address.id ?? generateEntityId(),
        label: address.label,
        value: address.value,
        type: address.type,
      }))
    : [];

  const generatedUrls = dbCardPreset.urls
    ? dbCardPreset.urls.map((url) => ({
        id: url.id ?? generateEntityId(),
        title: url.title,
        value: url.value,
        type: url.type,
        userValue: url.user_value ?? url.value,
      }))
    : [];

  const generatedPhoneNumbers = dbCardPreset.phone_numbers
    ? dbCardPreset.phone_numbers.map((phoneNumber) => ({
        id: phoneNumber.id ?? generateEntityId(),
        label: phoneNumber.label,
        value: phoneNumber.value,
        extension: phoneNumber.extension,
        countryCode: phoneNumber.country_code ?? undefined,
      }))
    : [];

  return {
    cardPresetId: dbCardPreset.card_preset_id,
    cardPresetName: dbCardPreset.card_preset_name,
    orgId: dbCardPreset.org_id,
    orgName: dbCardPreset.org_name,
    accreditations: dbCardPreset.accreditations,
    addresses: generatedAddresses,
    blinqMessageEnabled: dbCardPreset.blinq_message_enabled ?? false,
    cardIsEditable: dbCardPreset.card_is_editable ?? true,
    color: dbCardPreset.color || DEFAULT_CARD_COLOR,
    companyLogoImg: toCardImage(dbCardPreset.company_logo_img),
    coverPhotoImg: toCardImage(dbCardPreset.cover_photo_img),
    creationTimestampMs: dbCardPreset.creation_timestamp_ms,
    department: dbCardPreset.department,
    emails: generatedEmails,
    headline: dbCardPreset.headline,
    isInvisible: dbCardPreset.is_invisible ?? false,
    jobTitle: dbCardPreset.job_title,
    logoInQrUrl: dbCardPreset.logo_in_qr_url,
    logoIsInQr: dbCardPreset.logo_is_in_qr ?? false,
    layout: dbCardPreset.layout,
    layoutImagesLocked: dbCardPreset.layout_images_locked,
    phoneNumbers: generatedPhoneNumbers,
    presetEditable: dbCardPreset.preset_editable ?? true,
    profilePictureImg: toCardImage(dbCardPreset.profile_picture_img),
    referralLinkEnabled: dbCardPreset.referral_link_enabled ?? false,
    updatedTimestampMs: dbCardPreset.updated_timestamp_ms,
    urls: generatedUrls,
    whereWeMetEnabled: dbCardPreset.where_we_met_enabled || false,
    emailSignature: dbCardPreset.email_signature
      ? {
          qrCode: dbCardPreset.email_signature?.qr_code ?? true,
          companyLogo:
            dbCardPreset.email_signature?.company_logo ??
            Boolean(dbCardPreset.company_logo_img) ??
            false,
          profilePicture:
            dbCardPreset.email_signature?.profile_picture ??
            Boolean(dbCardPreset.profile_picture_img) ??
            false,
          // does the email signature need the cover photo img?
          headline:
            dbCardPreset.email_signature?.headline ?? Boolean(dbCardPreset.headline) ?? false,
          jobTitle:
            dbCardPreset.email_signature?.job_title ?? Boolean(dbCardPreset.job_title) ?? false,
          accreditations:
            dbCardPreset.email_signature?.accreditations ??
            Boolean(dbCardPreset.accreditations) ??
            false,
          orgName:
            dbCardPreset.email_signature?.org_name ?? Boolean(dbCardPreset.org_name) ?? false,
          phoneNumbers:
            dbCardPreset.email_signature?.phone_numbers ??
            fieldsToEmailSignature(generatedPhoneNumbers),
          emails: dbCardPreset.email_signature?.emails ?? fieldsToEmailSignature(generatedEmails),
          urls: dbCardPreset.email_signature?.urls ?? fieldsToEmailSignature(generatedUrls),
          addresses:
            dbCardPreset.email_signature?.addresses ?? fieldsToEmailSignature(generatedAddresses),
          customBanner: dbCardPreset.email_signature?.custom_banner ?? '',
        }
      : undefined,
    lockedFields: dbCardPreset.locked_fields ?? [],
    lockedFieldTypes: dbCardPreset.locked_field_types ?? [],
    allFieldTypesLocked: dbCardPreset.all_field_types_locked ?? false,
    lockedEmailSignatureFields: dbCardPreset.locked_email_signature_fields ?? [],
    allEmailSignatureFieldsLocked: dbCardPreset.all_email_signature_fields_locked ?? false,
    colorLocked: dbCardPreset.color_locked ?? false,
    qrCodeLocked: dbCardPreset.qr_code_locked ?? false,
    syncUpdates: dbCardPreset.sync_updates ?? true,
    fieldOrder: populateCardPresetFieldOrder({
      fieldOrder: dbCardPreset.field_order,
      addresses: generatedAddresses,
      emails: generatedEmails,
      urls: generatedUrls,
      phoneNumbers: generatedPhoneNumbers,
    }),
  };
}

export function toDBCardPreset(cardPreset: Partial<CardPreset>): Partial<DBCardPreset> {
  return {
    accreditations: cardPreset.accreditations,
    all_email_signature_fields_locked: cardPreset.allEmailSignatureFieldsLocked,
    all_field_types_locked: cardPreset.allFieldTypesLocked,
    blinq_message_enabled: cardPreset.blinqMessageEnabled,
    card_is_editable: cardPreset.cardIsEditable,
    card_preset_id: cardPreset.cardPresetId,
    card_preset_name: cardPreset.cardPresetName,
    color_locked: cardPreset.colorLocked,
    color: cardPreset.color,
    company_logo_img: toDBCardImage(cardPreset.companyLogoImg),
    // company_logo_url: cardPreset.companyLogoImg?.editedImageUrl || null, // dont backwards compat here.
    cover_photo_img: toDBCardImage(cardPreset.coverPhotoImg),
    creation_timestamp_ms: cardPreset.creationTimestampMs,
    department: cardPreset.department,
    headline: cardPreset.headline,
    is_invisible: cardPreset.isInvisible,
    job_title: cardPreset.jobTitle,
    layout: cardPreset.layout ?? CardLayout.Layout_1C,
    layout_images_locked: cardPreset.layoutImagesLocked,
    locked_email_signature_fields: cardPreset.lockedEmailSignatureFields,
    locked_field_types: cardPreset.lockedFieldTypes,
    locked_fields: cardPreset.lockedFields,
    logo_in_qr_url: cardPreset.logoInQrUrl,
    logo_is_in_qr: cardPreset.logoIsInQr,
    org_id: cardPreset.orgId,
    org_name: cardPreset.orgName,
    preset_editable: cardPreset.presetEditable,
    profile_picture_img: toDBCardImage(cardPreset.profilePictureImg),
    // profile_picture_url: cardPreset.profilePictureImg?.editedImageUrl || null, // don't backwards compat here
    // profile_picture_placement: cardPreset.companyLogoImg?.editedImageUrl ? "floating" : "banner",
    qr_code_locked: cardPreset.qrCodeLocked,
    referral_link_enabled: cardPreset.referralLinkEnabled,
    sync_updates: cardPreset.syncUpdates,
    updated_timestamp_ms: cardPreset.updatedTimestampMs,
    where_we_met_enabled: cardPreset.whereWeMetEnabled,
    emails: cardPreset.emails?.map((email) => ({
      id: email.id ?? generateEntityId(),
      label: email.label,
      value: email.value,
    })),
    addresses: cardPreset.addresses?.map((address) => ({
      id: address.id ?? generateEntityId(),
      label: address.label,
      value: address.value,
      type: address.type,
    })),
    urls: cardPreset.urls?.map((url) => ({
      id: url.id ?? generateEntityId(),
      title: url.title,
      value: url.value,
      type: url.type,
      user_value: url.userValue,
    })),
    phone_numbers: cardPreset.phoneNumbers?.map((phoneNumber) => ({
      id: phoneNumber.id ?? generateEntityId(),
      label: phoneNumber.label,
      value: phoneNumber.value,
      country_code: phoneNumber.countryCode ?? null,
      ...(phoneNumber.extension ? { extension: phoneNumber.extension } : {}),
    })),
    email_signature: cardPreset.emailSignature
      ? toDBEmailSignature(cardPreset.emailSignature)
      : undefined,
    field_order: cardPreset.fieldOrder,
  };
}

export function toCardSave(dbCardSave: DBCardSave): CardSave {
  return {
    cardId: dbCardSave.card_id,
    cardEditId: dbCardSave.card_edit_id,
    date: dbCardSave.date,
    location: dbCardSave.location,
    orgId: dbCardSave.org_id,
    timestampMs: dbCardSave.timestamp_ms,
    savedAt: dbCardSave.saved_at,
  };
}

export function toContactNote(dbContactNote: DBContactNote): ContactNote {
  return {
    contactId: dbContactNote.contact_id,
    contactNoteId: dbContactNote.contact_note_id,
    createTimestamp: dbContactNote.create_timestamp,
    createTimezone: dbContactNote.create_timezone,
    isInvisible: dbContactNote.is_invisible,
    note: dbContactNote.note,
    updateTimestamp: dbContactNote.update_timestamp,
    updateTimezone: dbContactNote.update_timezone,
    userId: dbContactNote.user_id,
  };
}

export function toDBContactNote(contactNote: ContactNote): DBContactNote {
  return {
    contact_id: contactNote.contactId,
    contact_note_id: contactNote.contactNoteId,
    create_timestamp: contactNote.createTimestamp,
    create_timezone: contactNote.createTimezone,
    is_invisible: contactNote.isInvisible,
    note: contactNote.note,
    update_timestamp: contactNote.updateTimestamp,
    update_timezone: contactNote.updateTimezone,
    user_id: contactNote.userId,
  };
}

export function toBBOrg(
  dbBbOrg: DBBBOrg,
  subscriptions: DBStripeSubscription[] | undefined = [],
): BBOrg {
  const bbOrgCommon = {
    canEmailAnyone: dbBbOrg.can_email_anyone,
    creationTimestampMs: dbBbOrg.creation_timestamp_ms,
    name: dbBbOrg.name,
    orgId: dbBbOrg.org_id,
    validEmailEndings: dbBbOrg.valid_email_endings,
    companySize: dbBbOrg.company_size ?? undefined,
    avatarUrl: dbBbOrg.avatar_url ?? undefined,
    defaultUserProvisioningPresetId: dbBbOrg.default_user_provisioning_preset_id,
    userProvisioningPresetFilters: dbBbOrg.user_provisioning_preset_filters?.map((filter) =>
      toUserProvisioningPresetFilter(filter),
    ),
    autoSendProvisionedUserActivationEmail:
      dbBbOrg.auto_send_provisioned_user_activation_email ?? true,
    autoExportContacts: dbBbOrg.auto_export_contacts ?? false,
  };

  if (dbBbOrg.is_demo_account !== true) {
    return {
      ...bbOrgCommon,
      isDemoAccount: false,
      adminUserId: dbBbOrg.admin_user_id,
      maxCards: dbBbOrg.max_cards,
      subscriptionId: dbBbOrg.subscription_id,
      overrideMaxCards: dbBbOrg.override_max_cards,
      paidForCardCount: dbBbOrg.paid_for_card_count,
      subscriptions: subscriptions.map((subscription) => toStripeSubscription(subscription)),
    };
  } else {
    return {
      ...bbOrgCommon,
      isDemoAccount: true,
    };
  }
}

export function toUserProvisioningPresetFilter(
  dbUserProvisioningPresetFilters: DBUserProvisioningPresetFilter,
): UserProvisioningPresetFilter {
  return {
    id: generateEntityId(),
    attributeFilters: dbUserProvisioningPresetFilters.attribute_filters.map(
      toUserProvisioningAttributeFilter,
    ),
    cardPresetId: dbUserProvisioningPresetFilters.card_preset_id,
    combinator: dbUserProvisioningPresetFilters.combinator,
  };
}

export function toUserProvisioningAttributeFilter(
  dbUserProvisioningAttributeFilter: DBUserProvisioningAttributeFilter,
): UserProvisioningAttributeFilter {
  return {
    id: generateEntityId(),
    attribute: dbUserProvisioningAttributeFilter.attribute,
    attributeValue: dbUserProvisioningAttributeFilter.attribute_value,
    operator: dbUserProvisioningAttributeFilter.operator,
  };
}

export function toDBUserProvisioningPresetFilter(
  userProvisioningPresetFilters: UserProvisioningPresetFilter,
): DBUserProvisioningPresetFilter {
  return sanitizeForDb({
    attribute_filters: userProvisioningPresetFilters.attributeFilters.map(
      toDBUserProvisioningAttributeFilter,
    ),
    card_preset_id: userProvisioningPresetFilters.cardPresetId,
    combinator: userProvisioningPresetFilters.combinator,
  });
}

export function toDBUserProvisioningAttributeFilter(
  userProvisioningAttributeFilter: UserProvisioningAttributeFilter,
): DBUserProvisioningAttributeFilter {
  return {
    attribute: userProvisioningAttributeFilter.attribute,
    attribute_value: userProvisioningAttributeFilter.attributeValue,
    operator: userProvisioningAttributeFilter.operator,
  };
}

export function toUserTags(dbUserTags: DBUserTags): UserTags {
  return {
    canAddCustomColor: dbUserTags.can_add_custom_color ?? dbUserTags.is_premium_subscriber ?? false,
    canAddLogoInQr: dbUserTags.can_add_logo_in_qr ?? false,
    canCreateMultipleCards: dbUserTags.can_create_multiple_cards ?? false,
    canDisplayPhoneNumber: dbUserTags.can_display_phone_number ?? false,
    canRemoveBlinqTag: dbUserTags.can_remove_blinq_tag ?? false,
    canRemoveCardReferralLink: dbUserTags.can_remove_card_referral_link ?? false,
    isPremiumSubscriber: dbUserTags.is_premium_subscriber ?? false,
    isWebPremiumSubscriber: dbUserTags.is_web_premium_subscriber ?? false,
    isBbEmployee: dbUserTags.is_bb_employee ?? false,
    isBlinqEmployee: dbUserTags.is_blinq_employee ?? false,
    canBookOnboarding: dbUserTags.can_book_onboarding ?? true,
    canExportContacts: dbUserTags.can_export_contacts ?? false,
  };
}

export function toStripeSubscription(
  dbStripeSubscription: DBStripeSubscription,
): StripeSubscription {
  return {
    created: dbStripeSubscription.created,
    canceledAt: dbStripeSubscription.canceled_at,
    cancelAt: dbStripeSubscription.cancel_at,
    cancelAtPeriodEnd: dbStripeSubscription.cancel_at_period_end,
    currentPeriodEnd: dbStripeSubscription.current_period_end,
    currentPeriodStart: dbStripeSubscription.current_period_start,
    endedAt: dbStripeSubscription.ended_at,
    metadata: dbStripeSubscription.metadata,
    price: dbStripeSubscription.price,
    prices: dbStripeSubscription.prices,
    product: dbStripeSubscription.product,
    quantity: dbStripeSubscription.quantity,
    role: dbStripeSubscription.role,
    status: dbStripeSubscription.status,
    stripeLink: dbStripeSubscription.stripeLink,
    trialEnd: dbStripeSubscription.trial_end,
    trialStart: dbStripeSubscription.trial_start,
  };
}

export function toPersonalAccessToken(
  dbPersonalAccessToken: DBPersonalAccessToken,
): PersonalAccessToken {
  return {
    id: dbPersonalAccessToken.id,
    orgId: dbPersonalAccessToken.org_id,
    userId: dbPersonalAccessToken.user_id,
    createdAt: dbPersonalAccessToken.created_at,
    lastUsedAt: dbPersonalAccessToken.last_used_at,
    name: dbPersonalAccessToken.name,
    metadata: dbPersonalAccessToken.metadata ?? {},
  };
}
export function toIntegration(integration: DBIntegration): Integration {
  const baseIntegration = {
    actions: integration.actions,
    integrationId: integration.integration_id,
    businessDomain: integration.business_domain,
    userId: integration.user_id,
    orgId: integration.org_id,
    createdAt: integration.created_at,
    updatedAt: integration.updated_at,
    deletedAt: integration.deleted_at,
    service: integration.service,
  };

  switch (integration.service) {
    case IntegrationServices.HUBSPOT:
      return {
        ...baseIntegration,
        service: IntegrationServices.HUBSPOT,
        settings: {
          autoExportContacts: integration.settings.auto_export_contacts,
          autoSyncContacts: integration.settings.auto_sync_contacts,
        },
        data: {},
      };
    case IntegrationServices.SALESFORCE:
      return {
        ...baseIntegration,
        service: IntegrationServices.SALESFORCE,
        settings: {
          autoExportContacts: integration.settings.auto_export_contacts,
          autoSyncContacts: integration.settings.auto_sync_contacts,
          exportToLeads: integration.settings.export_to_leads ?? false,
        },
        data: {},
      };
    case IntegrationServices.ZAPIER:
      return {
        ...baseIntegration,
        service: IntegrationServices.ZAPIER,
        settings: {
          autoExportContacts: integration.settings.auto_export_contacts,
        },
        data: {
          targetUrls: integration.data.target_urls,
        },
      };
  }
}

export function toDBIntegration(integration: Integration): DBIntegration {
  const baseIntegration = {
    actions: integration.actions,
    integration_id: integration.integrationId,
    business_domain: integration.businessDomain,
    user_id: integration.userId,
    org_id: integration.orgId,
    created_at: integration.createdAt,
    updated_at: integration.updatedAt,
    deleted_at: integration.deletedAt,
    data: {
      target_urls: integration.data.targetUrls,
    },
  };
  switch (integration.service) {
    case IntegrationServices.HUBSPOT:
      return {
        ...baseIntegration,
        service: IntegrationServices.HUBSPOT,
        settings: {
          auto_export_contacts: integration.settings.autoExportContacts,
          auto_sync_contacts: integration.settings.autoSyncContacts,
        },
        data: {},
      };
    case IntegrationServices.SALESFORCE:
      return {
        ...baseIntegration,
        service: IntegrationServices.SALESFORCE,
        settings: {
          auto_export_contacts: integration.settings.autoExportContacts,
          auto_sync_contacts: integration.settings.autoSyncContacts,
          export_to_leads: integration.settings.exportToLeads,
        },
        data: {},
      };
    case IntegrationServices.ZAPIER:
      return {
        ...baseIntegration,
        service: IntegrationServices.ZAPIER,
        data: { target_urls: integration.data.targetUrls },
        settings: {
          auto_export_contacts: integration.settings.autoExportContacts,
        },
      };
  }
}

export function sanitizeForDb<T>(obj: T): T {
  // @ts-expect-error - TODO: Fix type error
  return Object.entries(obj).reduce((card, [key, value]) => {
    if (value === undefined) return card;
    else return { ...card, [key]: value };
  }, {} as T);
}

function transformObjectValues<T, O>(
  obj: T,
  transformer: (value: ValueOf<T>) => O,
): Record<keyof T, O> {
  // @ts-expect-error - TODO: Fix type error
  return Object.entries(obj).reduce(
    (card, [key, value]) => {
      // @ts-expect-error - TODO: Fix type error
      return { ...card, [key]: transformer(value) };
    },
    {} as Record<keyof T, O>,
  );
}

type ValueOf<T> = T[keyof T];

export function toVirtualBackgroundImage(
  dbVirtualBackgroundImage: DBVirtualBackgroundImage,
): VirtualBackgroundImage {
  return {
    id: dbVirtualBackgroundImage.id,
    imageUrl: dbVirtualBackgroundImage.image_url,
    userIds: dbVirtualBackgroundImage.user_ids || [],
    unclaimedCardIds: dbVirtualBackgroundImage.unclaimed_card_ids || [],
  };
}

export function toDBVirtualBackgroundImage(
  virtualBackgroundImage: VirtualBackgroundImage,
): DBVirtualBackgroundImage {
  return {
    id: virtualBackgroundImage.id,
    image_url: virtualBackgroundImage.imageUrl,
    user_ids: virtualBackgroundImage.userIds,
    unclaimed_card_ids: virtualBackgroundImage.unclaimedCardIds,
  };
}

export function toDBVirtualBackground(vbackground: VirtualBackground): DBVirtualBackground {
  return {
    id: vbackground.id,
    org_id: vbackground.orgId,
    user_id: vbackground.userId,
    created_at: vbackground.createdAt,
    updated_at: vbackground.updatedAt,
    deleted_at: vbackground.deletedAt,
    card_id: vbackground.cardId,
    title: vbackground.title,
    base_image: vbackground.baseImage,
    image: vbackground.image,
    created_by: vbackground.createdBy,
    qr_code_position: vbackground.qrCodePosition,
    notification_email_sent_at: vbackground.notificationEmailSentAt,
    download_token: vbackground.downloadToken,
    download_token_generated_at: vbackground.downloadTokenGeneratedAt,
    downloaded_at: vbackground.downloadedAt,
    primary_field: vbackground.primaryField,
    secondary_fields: vbackground.secondaryFields,
    is_logo_in_qr: vbackground.isLogoInQr,
    use_card_logo: vbackground.useCardLogo,
    logo_image: vbackground.logoImage,
    qr_code_x_coord: vbackground.qrCodeXCoord,
    qr_code_y_coord: vbackground.qrCodeYCoord,
    show_scan_instructions: vbackground.showScanInstructions,
    information_container_size: vbackground.informationContainerSize,
  };
}

export function toVirtualBackground(db: DBVirtualBackground): VirtualBackground {
  return {
    id: db.id,
    orgId: db.org_id,
    userId: db.user_id,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
    deletedAt: db.deleted_at,
    cardId: db.card_id,
    title: db.title,
    baseImage: db.base_image,
    image: db.image,
    createdBy: db.created_by,
    qrCodePosition: db.qr_code_position,
    notificationEmailSentAt: db.notification_email_sent_at,
    downloadToken: db.download_token,
    downloadTokenGeneratedAt: db.download_token_generated_at,
    downloadedAt: db.downloaded_at,
    primaryField: db.primary_field ?? '',
    secondaryFields: db.secondary_fields ?? ['', '', '', ''],
    isLogoInQr: db.is_logo_in_qr ?? false,
    useCardLogo: db.use_card_logo ?? false,
    logoImage: db.logo_image ?? '',
    qrCodeXCoord: db.qr_code_x_coord,
    qrCodeYCoord: db.qr_code_y_coord,
    showScanInstructions: db.show_scan_instructions ?? true,
    informationContainerSize: db.information_container_size ?? 'medium',
  };
}

export function toEmailSignatureV2({
  id,
  created_at,
  updated_at,
  deleted_at,
  created_by,
  org_id,
  user_id,
  recipient_email,
  activated_at,
  activation_code,
  send_activation_email_at,
  title,
  personal_details,
  contact_details,
  contact_details_order,
  social_links,
  social_links_order,
  design,
  banner: dbBanner,
  environmental_footer,
  legal_disclaimer,
  save_contact,
  images,
  is_team_email_signature,
  imported_card_id,
  prevent_editing_signature,
  google_workspace_integration_status,
}: DBEmailSignatureV2): EmailSignatureV2 {
  const contactDetailsFields: EmailSignatureContactDetailsFields = {};
  Object.values(contact_details).forEach((contactDetailsField) => {
    contactDetailsFields[contactDetailsField.id] =
      toEmailSignatureContactDetailsField(contactDetailsField);
  });

  const socialLinks: EmailSignatureSocialLinks = {};
  Object.values(social_links).forEach((socialLink) => {
    socialLinks[socialLink.id] = toEmailSignatureSocialLink(socialLink);
  });

  const clientImages: EmailSignaturesImages = [];
  Object.values(images).forEach((image, index) => {
    clientImages[index] = toEmailSignatureImage(image);
  });

  let banner;
  if (dbBanner === undefined) banner = dbBanner;
  else banner = toEmailSignatureBanner(dbBanner);

  let environmentalFooter;
  if (environmental_footer === undefined) environmentalFooter = environmental_footer;
  else environmentalFooter = toEmailSignatureEnvironmentalFooter(environmental_footer);

  let legalDisclaimer;
  if (legal_disclaimer === undefined) legalDisclaimer = legal_disclaimer;
  else legalDisclaimer = toEmailSignatureLegalDisclaimer(legal_disclaimer);

  let saveContact;
  if (save_contact === undefined) saveContact = save_contact;
  else saveContact = toEmailSignatureSaveContact(save_contact);

  return {
    id,
    title,
    createdAt: created_at,
    updatedAt: updated_at,
    deletedAt: deleted_at,
    createdBy: {
      id: created_by.id,
      firstName: created_by.first_name,
      lastName: created_by.last_name,
    },
    isTeamEmailSignature: is_team_email_signature,
    orgId: org_id,
    userId: user_id,
    recipientEmail: recipient_email,
    activatedAt: activated_at,
    activationCode: activation_code,
    sendActivationEmailAt: send_activation_email_at,
    personalDetails: toEmailSignaturePersonalDetails(personal_details),
    contactDetails: contactDetailsFields,
    contactDetailsOrder: contact_details_order,
    socialLinks,
    socialLinksOrder: social_links_order,
    banner,
    environmentalFooter,
    legalDisclaimer,
    saveContact,
    design: toEmailSignatureDesign(design),
    images: clientImages,
    importedCardId: imported_card_id,
    preventEditingSignature: prevent_editing_signature,
    googleWorkspaceIntegrationStatus: {
      success: google_workspace_integration_status?.success,
      message: google_workspace_integration_status?.message,
      updatedAt: google_workspace_integration_status?.updated_at,
      firstSyncedAt: google_workspace_integration_status?.first_synced_at,
    },
  };
}

function toEmailSignaturePersonalDetails({
  job_title,
  company,
  headline,
  first_name,
  last_name,
}: DBEmailSignaturePersonalDetailsFields): EmailSignaturePersonalDetailsFields {
  return {
    jobTitle: job_title,
    company,
    headline: headline,
    firstName: first_name,
    lastName: last_name,
  };
}

function toEmailSignatureContactDetailsField({
  id,
  type,
  label,
  value,
  base_icon,
  field_width,
  icon_url,
  title,
  extension,
  country_code,
}: DBEmailSignatureContactDetailsField): EmailSignatureContactDetailsField {
  return {
    id,
    type,
    label,
    value,
    baseIcon: base_icon,
    fieldWidth: field_width,
    iconUrl: icon_url,
    title,
    extension,
    countryCode: country_code,
  };
}

function toEmailSignatureSocialLink({
  id,
  type,
  value,
  icon_url,
}: DBEmailSignatureSocialLink): EmailSignatureSocialLink {
  return {
    id,
    type,
    value,
    iconUrl: icon_url,
  };
}

function toEmailSignatureImage({
  id,
  external_link,
  image_url,
  shape,
  size,
  type,
  is_primary,
  original_image_url,
  crop,
}: DBEmailSignatureImage): EmailSignatureImage {
  return {
    id,
    imageUrl: image_url,
    externalLink: external_link,
    shape,
    size,
    type,
    isPrimary: is_primary,
    originalImageUrl: original_image_url,
    crop,
  };
}

function toEmailSignatureBanner({
  id,
  external_link,
  image_url,
  size,
  crop,
  original_image_url,
}: DBEmailSignatureBanner): EmailSignatureBanner {
  return sanitizeForDb({
    id,
    imageUrl: image_url,
    externalLink: external_link,
    size,
    crop,
    originalImageUrl: original_image_url,
  });
}

function toEmailSignatureEnvironmentalFooter({
  id,
  value,
  base_icon,
  icon_url,
}: DBEmailSignatureEnvironmentalFooter): EmailSignatureEnvironmentalFooter {
  return {
    id,
    value,
    baseIcon: base_icon,
    iconUrl: icon_url,
  };
}

function toEmailSignatureLegalDisclaimer({
  id,
  value,
  font_size,
}: DBEmailSignatureLegalDisclaimer): EmailSignatureLegalDisclaimer {
  return {
    id,
    value,
    fontSize: font_size,
  };
}

function toEmailSignatureSaveContact({
  card_id,
  button_color_use_theme_color,
  button_color,
  display_save_contact,
}: DBEmailSignatureSaveContact): EmailSignatureSaveContact {
  return {
    cardId: card_id,
    buttonColorUseThemeColor: button_color_use_theme_color,
    buttonColor: button_color,
    displaySaveContact: display_save_contact,
  };
}

function toEmailSignatureDesign({
  font,
  theme_color,
  contact_details_display_icon,
  contact_details_icon_use_theme_color,
  contact_details_icon_color,

  social_links_fill_style,
  social_links_shape,
  social_links_size,
  social_links_color_mode,
  social_links_color,
}: DBEmailSignatureDesign): EmailSignatureDesign {
  return {
    font: font,
    themeColor: theme_color,
    contactDetailsDisplayIcon: contact_details_display_icon,
    contactDetailsIconUseThemeColor: contact_details_icon_use_theme_color,
    contactDetailsIconColor: contact_details_icon_color,
    socialLinksFillStyle: social_links_fill_style,
    socialLinksShape: social_links_shape,
    socialLinksSize: social_links_size,
    socialLinksColorMode: social_links_color_mode,
    socialLinksColor: social_links_color,
  };
}

export function toDBEmailSignatureV2({
  id,
  title,
  createdAt,
  updatedAt,
  deletedAt,
  createdBy,
  orgId,
  userId,
  recipientEmail,
  activatedAt,
  activationCode,
  sendActivationEmailAt,
  personalDetails,
  contactDetails,
  contactDetailsOrder,
  socialLinks,
  socialLinksOrder,
  banner,
  environmentalFooter,
  legalDisclaimer,
  saveContact,
  design,
  images,
  isTeamEmailSignature,
  importedCardId,
  preventEditingSignature,
  googleWorkspaceIntegrationStatus,
}: EmailSignatureV2): DBEmailSignatureV2 {
  const dbContactDetailsFields: DBEmailSignatureContactDetailsFields = {};
  Object.values(contactDetails).forEach((dbContactDetailsField) => {
    dbContactDetailsFields[dbContactDetailsField.id] =
      toDBEmailSignatureContactDetailsField(dbContactDetailsField);
  });

  const dbSocialLinks: DBEmailSignatureSocialLinks = {};
  Object.values(socialLinks).forEach((dbSocialLink) => {
    dbSocialLinks[dbSocialLink.id] = toDBEmailSignatureSocialLink(dbSocialLink);
  });

  const dbImages: DBEmailSignatureImages = [];
  Object.values(images).forEach((dbImage, index) => {
    dbImages[index] = toDBEmailSignatureImage(dbImage);
  });

  let dbBanner;
  if (banner === undefined) dbBanner = banner;
  else dbBanner = toDBEmailSignatureBanner(banner);

  let dbEnvironmentalFooter;
  if (environmentalFooter === undefined) dbEnvironmentalFooter = environmentalFooter;
  else dbEnvironmentalFooter = toDBEmailSignatureEnvironmentalFooter(environmentalFooter);

  let dbLegalDisclaimer;
  if (legalDisclaimer === undefined) dbLegalDisclaimer = legalDisclaimer;
  else dbLegalDisclaimer = toDBEmailSignatureLegalDisclaimer(legalDisclaimer);
  let dbSaveContact;
  if (saveContact === undefined) dbSaveContact = saveContact;
  else dbSaveContact = toDBEmailSignatureSaveContact(saveContact);

  return sanitizeForDb({
    id,
    title,
    created_at: createdAt,
    updated_at: updatedAt,
    deleted_at: deletedAt,
    created_by: {
      id: createdBy.id,
      first_name: createdBy.firstName,
      last_name: createdBy.lastName,
    },
    is_team_email_signature: isTeamEmailSignature,
    org_id: orgId,
    user_id: userId,
    recipient_email: recipientEmail,
    activated_at: activatedAt,
    activation_code: activationCode,
    send_activation_email_at: sendActivationEmailAt,
    personal_details: toDBEmailSignaturePersonalDetails(personalDetails),
    contact_details: dbContactDetailsFields,
    contact_details_order: contactDetailsOrder,
    social_links: dbSocialLinks,
    social_links_order: socialLinksOrder,
    banner: dbBanner,
    environmental_footer: dbEnvironmentalFooter,
    legal_disclaimer: dbLegalDisclaimer,
    save_contact: dbSaveContact,
    design: toDBEmailSignatureDesign(design),
    images: dbImages,
    imported_card_id: importedCardId,
    prevent_editing_signature: preventEditingSignature,
    google_workspace_integration_status: {
      success: googleWorkspaceIntegrationStatus?.success,
      message: googleWorkspaceIntegrationStatus?.message,
      updated_at: googleWorkspaceIntegrationStatus?.updatedAt,
      first_synced_at: googleWorkspaceIntegrationStatus?.firstSyncedAt,
    },
  });
}

export function toDBTeamEmailSignatureBulkCreateRequest({
  id,
  emailSignatureId,
  requestedAt,
  timezone,
  userId,
  orgId,
  instructionsSentAt,
  blinqNotificationSentAt,
}: TeamEmailSignatureBulkCreateRequest): DBTeamEmailSignatureBulkCreateRequest {
  return {
    id,
    email_signature_id: emailSignatureId,
    requested_at: requestedAt,
    timezone,
    user_id: userId,
    org_id: orgId,
    instructions_sent_at: instructionsSentAt,
    blinq_notification_sent_at: blinqNotificationSentAt,
  };
}

export function toTeamEmailSignatureBulkCreateRequest({
  email_signature_id,
  requested_at,
  timezone,
  id,
  user_id,
  org_id,
  instructions_sent_at,
  blinq_notification_sent_at,
}: DBTeamEmailSignatureBulkCreateRequest): TeamEmailSignatureBulkCreateRequest {
  return {
    id,
    emailSignatureId: email_signature_id,
    requestedAt: requested_at,
    timezone,
    userId: user_id,
    orgId: org_id,
    instructionsSentAt: instructions_sent_at,
    blinqNotificationSentAt: blinq_notification_sent_at,
  };
}

function toDBEmailSignaturePersonalDetails({
  jobTitle,
  company,
  headline,
  firstName,
  lastName,
}: EmailSignaturePersonalDetailsFields): DBEmailSignaturePersonalDetailsFields {
  return sanitizeForDb({
    job_title: jobTitle,
    company,
    headline: headline,
    first_name: firstName,
    last_name: lastName,
  });
}

function toDBEmailSignatureContactDetailsField({
  id,
  type,
  label,
  value,
  baseIcon,
  fieldWidth,
  iconUrl,
  title,
  countryCode,
  extension,
}: EmailSignatureContactDetailsField): DBEmailSignatureContactDetailsField {
  return sanitizeForDb({
    id,
    type,
    label,
    value,
    base_icon: baseIcon,
    field_width: fieldWidth,
    icon_url: iconUrl,
    title,
    extension,
    country_code: countryCode,
  });
}

function toDBEmailSignatureSocialLink({
  id,
  type,
  value,
  iconUrl,
}: EmailSignatureSocialLink): DBEmailSignatureSocialLink {
  return {
    id,
    type,
    value,
    icon_url: iconUrl,
  };
}

function toDBEmailSignatureImage({
  id,
  imageUrl,
  externalLink,
  shape,
  size,
  type,
  isPrimary,
  originalImageUrl,
  crop,
}: EmailSignatureImage): DBEmailSignatureImage {
  return sanitizeForDb({
    id,
    image_url: imageUrl,
    external_link: externalLink,
    shape,
    size,
    type,
    is_primary: isPrimary,
    original_image_url: originalImageUrl,
    crop: crop ? sanitizeForDb(crop) : crop,
  });
}

function toDBEmailSignatureBanner({
  id,
  externalLink,
  imageUrl,
  size,
  originalImageUrl,
  crop,
}: EmailSignatureBanner): DBEmailSignatureBanner {
  return sanitizeForDb({
    id,
    image_url: imageUrl,
    external_link: externalLink,
    original_image_url: originalImageUrl,
    crop: crop ? sanitizeForDb(crop) : crop,
    size,
  });
}

function toDBEmailSignatureEnvironmentalFooter({
  id,
  value,
  baseIcon,
  iconUrl,
}: EmailSignatureEnvironmentalFooter): DBEmailSignatureEnvironmentalFooter {
  return {
    id,
    value,
    base_icon: baseIcon,
    icon_url: iconUrl,
  };
}

function toDBEmailSignatureSaveContact({
  cardId,
  buttonColorUseThemeColor,
  buttonColor,
  displaySaveContact,
}: EmailSignatureSaveContact): DBEmailSignatureSaveContact {
  return {
    card_id: cardId,
    button_color_use_theme_color: buttonColorUseThemeColor,
    button_color: buttonColor,
    display_save_contact: displaySaveContact,
  };
}

function toDBEmailSignatureLegalDisclaimer({
  id,
  value,
  fontSize,
}: EmailSignatureLegalDisclaimer): DBEmailSignatureLegalDisclaimer {
  return {
    id,
    value,
    font_size: fontSize,
  };
}

function toDBEmailSignatureDesign({
  font,
  themeColor,
  contactDetailsDisplayIcon,
  contactDetailsIconUseThemeColor,
  contactDetailsIconColor,
  socialLinksFillStyle,
  socialLinksShape,
  socialLinksSize,
  socialLinksColorMode,
  socialLinksColor,
}: EmailSignatureDesign): DBEmailSignatureDesign {
  return sanitizeForDb({
    font: font,
    theme_color: themeColor,
    contact_details_display_icon: contactDetailsDisplayIcon,
    contact_details_icon_use_theme_color: contactDetailsIconUseThemeColor,
    contact_details_icon_color: contactDetailsIconColor,
    social_links_fill_style: socialLinksFillStyle,
    social_links_shape: socialLinksShape,
    social_links_size: socialLinksSize,
    social_links_color_mode: socialLinksColorMode,
    social_links_color: socialLinksColor,
  });
}
